import {
  Card, CardHeader, CardBody, StatusBadge, Loading, ErrorNotice, EmptyState, CopyButton,
} from './ui/index.js';
import { useResource } from '../hooks/useApi.js';
import { formatMoney, formatExactTimestamp } from '../lib/format.js';

const ATTEMPT_STATUS = { approved: 'succeeded', review: 'processing' };
/** A saved call's outcome is either a charge's (approved…) or a refund's (succeeded…). */
const EXCHANGE_STATUS = {
  approved: 'succeeded', succeeded: 'succeeded', review: 'processing', pending: 'processing',
  timeout: 'processing', unknown: 'processing',
};
const OPERATION_LABEL = { session: 'Order created', charge: 'Charge', refund: 'Refund', void: 'Void' };

/** Collapsed by default: a gateway response is for the person who already knows they need it. */
function JsonBlock({ label, value }) {
  const text = JSON.stringify(value, null, 2);
  return (
    <details className="rounded-lg border border-line">
      <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-ink-soft">{label}</summary>
      <div className="border-t border-line p-3">
        <div className="mb-2 flex justify-end"><CopyButton value={text} label="Copy JSON" /></div>
        <pre className="max-h-80 overflow-auto rounded-md bg-surface-2 p-3 text-[11px] leading-relaxed text-ink-soft">{text}</pre>
      </div>
    </details>
  );
}

function RequestFields({ fields }) {
  return (
    <dl className="divide-y divide-line-soft rounded-lg border border-line">
      {fields.map((f) => (
        <div key={f.path} className="grid gap-1 px-3.5 py-2.5 sm:grid-cols-[14rem_minmax(0,1fr)] sm:gap-4">
          <dt className="min-w-0 text-xs font-medium text-muted">
            {f.label}
            {/* The gateway's own name for the field, for quoting to its support team. */}
            <span className="mt-0.5 block break-all font-mono text-[10px] font-normal text-faint">{f.path}</span>
          </dt>
          <dd className="min-w-0">
            {f.hidden && <span className="text-xs italic text-muted">{f.value}</span>}
            {!f.hidden && f.value == null && <span className="text-xs text-faint">Not sent</span>}
            {!f.hidden && f.value != null && (
              <span className="whitespace-pre-wrap break-words font-mono text-[13px] text-ink">{f.value}</span>
            )}
            {/* Expected, not an error: every request is shortened to the gateway's limits before it is sent. */}
            {f.truncatedFrom && (
              <p className="mt-0.5 text-[11px] text-muted">
                Shortened to {String(f.value).length} of {f.truncatedFrom} characters, the most this field accepts.
              </p>
            )}
            {f.note && <p className="mt-0.5 text-[11px] text-muted">{f.note}</p>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function RequestBlock({ request }) {
  return (
    <div className="space-y-2.5">
      <p className="text-[11px] text-muted">
        Transaction type <span className="font-mono text-ink-soft">{request.transactionType}</span>
      </p>
      <RequestFields fields={request.fields} />
      <JsonBlock label="Request body as sent (JSON)" value={request.body} />
    </div>
  );
}

/** The audit copies: exactly what went out and what came back, saved at the time. */
function SavedExchanges({ exchanges, name }) {
  return (
    <section>
      <p className="eyebrow mb-1">Saved requests and responses</p>
      <p className="mb-3 text-xs leading-relaxed text-muted">
        Saved at the moment each call was made. Credentials and card details are removed before saving.
      </p>
      {exchanges.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line px-3.5 py-3 text-xs text-muted">
          No saved copies — this payment&rsquo;s calls were made before saving began. The rebuilt request below
          shows what was sent.
        </p>
      ) : (
        <div className="space-y-3">
          {exchanges.map((x) => (
            <div key={x.id} className="space-y-3 rounded-lg border border-line p-3.5">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <span className="font-medium text-ink">{OPERATION_LABEL[x.operation] ?? x.operation}</span>
                {x.attemptNumber != null && <span className="text-muted">Attempt #{x.attemptNumber}</span>}
                {x.outcome && <StatusBadge status={EXCHANGE_STATUS[x.outcome] ?? 'failed'} label={x.outcome} />}
                {x.providerTransactionId && <span className="font-mono text-ink-soft">Transaction {x.providerTransactionId}</span>}
                <span className="ml-auto text-muted">{formatExactTimestamp(x.createdAt)}</span>
              </div>

              {/* One operation can be several calls — a token fetched, then the capture itself. */}
              {x.calls.map((call, i) => (
                <div key={`${x.id}-${i}`} className="space-y-2">
                  <p className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-muted">
                    <span className="font-mono font-medium text-ink-soft">{call.method}</span>
                    <span className="break-all font-mono">{call.url}</span>
                    {call.status != null && <span className="tabular">· {call.status}</span>}
                    {call.durationMs != null && <span className="tabular">· {call.durationMs}ms</span>}
                  </p>
                  {call.request != null
                    ? <JsonBlock label="Request as sent" value={call.request} />
                    : <p className="text-[11px] text-faint">No request body.</p>}
                  {call.response != null
                    ? <JsonBlock label={`${name} response as received`} value={call.response} />
                    : <p className="text-[11px] text-faint">No response — {call.error === 'timeout' ? `${name} did not answer in time` : (call.error ?? 'nothing came back')}.</p>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * What this payment told its gateway, field by field, and what came back.
 *
 * Read-only by construction — neither endpoint behind it has a write path. It
 * is here for one conversation: a gateway's risk team asking what was sent on a
 * transaction, and somebody needing to answer from the record rather than from
 * memory.
 *
 * `path` is the endpoint to read: the merchant's dashboard and the platform
 * admin share this card. Only the admin's answer carries `exchanges`, the saved
 * copies, and the card shows them whenever they are there.
 */
export function GatewayDataCard({ path }) {
  const { data, error, loading, refetch } = useResource(path);

  if (loading) {
    return <Card><CardHeader title="Sent to the gateway" /><Loading label="Loading what was sent…" /></Card>;
  }
  if (error) {
    return (
      <Card>
        <CardHeader title="Sent to the gateway" />
        <ErrorNotice error={error} onRetry={refetch} title="Could not load what was sent" className="m-4" />
      </Card>
    );
  }

  const name = data.providerName ?? 'the gateway';
  const refundsSent = data.refunds.filter((r) => r.requests.length > 0 || r.response);
  const nothingSent = data.attempts.length === 0 && refundsSent.length === 0;

  return (
    <Card>
      <CardHeader
        title={`Sent to ${name}`}
        blurb="Every field this payment put in its requests, and what came back. Credentials and the card token are never shown."
        actions={<StatusBadge status={data.environment} />}
      />
      <CardBody className="space-y-6">
        {Array.isArray(data.exchanges) && <SavedExchanges exchanges={data.exchanges} name={name} />}

        {nothingSent ? (
          <EmptyState
            title="Nothing has been sent yet"
            blurb={`The customer has not submitted a card, so ${name} has not been contacted about this payment.`}
          />
        ) : (
          <>
            {data.charge ? (
              <section>
                <p className="eyebrow mb-1">Charge request</p>
                <p className="mb-3 text-xs leading-relaxed text-muted">
                  Sent on every attempt below. Rebuilt from this payment by the same rules the real request is built
                  with, so it shows what went out even for payments made before requests were saved.
                </p>
                <RequestBlock request={data.charge} />
                {data.caveats.map((c) => <p key={c} className="mt-2 text-[11px] text-muted">{c}</p>)}
              </section>
            ) : (
              <p className="text-xs text-muted">
                Requests to {name} cannot be shown field by field yet. Its stored responses are below.
              </p>
            )}

            {data.attempts.length > 0 && (
              <section>
                <p className="eyebrow mb-2">Attempts and responses</p>
                <div className="space-y-3">
                  {data.attempts.map((a) => (
                    <div key={a.id} className="rounded-lg border border-line p-3.5">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                        <span className="font-medium text-ink">#{a.attemptNumber}</span>
                        <StatusBadge status={ATTEMPT_STATUS[a.outcome] ?? 'failed'} label={a.outcome} />
                        {a.providerTransactionId && (
                          <span className="font-mono text-ink-soft">Transaction {a.providerTransactionId}</span>
                        )}
                        {a.providerCode && <span className="text-muted">Code {a.providerCode}</span>}
                        <span className="ml-auto text-muted">{formatExactTimestamp(a.createdAt)}</span>
                      </div>
                      {a.failure && <p className="mt-1.5 text-xs text-ink-soft">{a.failure.message}</p>}
                      <div className="mt-2.5">
                        {a.response
                          ? <JsonBlock label={`${name} response (as stored)`} value={a.response} />
                          : <p className="text-[11px] text-faint">No response was stored — {name} did not answer in time.</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {data.refunds.length > 0 && (
              <section>
                <p className="eyebrow mb-2">Refunds</p>
                <div className="space-y-3">
                  {data.refunds.map((r) => (
                    <div key={r.id} className="space-y-2.5 rounded-lg border border-line p-3.5">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                        <span className="tabular font-medium text-ink">{formatMoney(r.amount, r.currency)}</span>
                        <StatusBadge status={r.status} />
                        {r.method === 'void' && <span className="text-muted">Voided before settlement</span>}
                        {r.providerRefundId && <span className="font-mono text-ink-soft">Transaction {r.providerRefundId}</span>}
                        <span className="ml-auto text-muted">{formatExactTimestamp(r.createdAt)}</span>
                      </div>
                      {r.source === 'provider_webhook' && (
                        <p className="text-xs text-muted">Made in {name}&rsquo;s own portal — DistrxPay sent nothing for it.</p>
                      )}
                      {r.requests.map((req) => <RequestBlock key={req.transactionType} request={req} />)}
                      {r.response && <JsonBlock label={`${name} response (as stored)`} value={r.response} />}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {(data.neverSent.length > 0 || data.sentFromBrowser.length > 0) && (
          <div className="grid gap-4 border-t border-line pt-4 sm:grid-cols-2">
            {data.neverSent.length > 0 && (
              <div>
                <p className="eyebrow mb-1">Never sent to {name}</p>
                <p className="text-xs leading-relaxed text-ink-soft">{data.neverSent.join(', ')}.</p>
              </div>
            )}
            {data.sentFromBrowser.length > 0 && (
              <div>
                <p className="eyebrow mb-1">Sent by the customer&rsquo;s browser</p>
                <p className="text-xs leading-relaxed text-ink-soft">
                  {data.sentFromBrowser.join(', ')} — straight to {name}, never through DistrxPay, so they cannot be shown here.
                </p>
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default GatewayDataCard;
